import React, { useEffect, useState, useCallback } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Lock, LogOut, RefreshCw, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { adminLogin, adminListBookings, adminUpdateBooking, adminStats } from "../lib/api";

const TOKEN_KEY = "ncchill_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [logging, setLogging] = useState(false);

  if (!token) {
    return <LoginScreen onLogin={async (p) => {
      setLogging(true);
      try {
        const { data } = await adminLogin(p);
        sessionStorage.setItem(TOKEN_KEY, data.token);
        setToken(data.token);
        toast.success("Welcome back, admin");
      } catch {
        toast.error("Invalid admin password");
      } finally {
        setLogging(false);
      }
    }} password={password} setPassword={setPassword} logging={logging} />;
  }
  return <Dashboard token={token} onLogout={() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  }} />;
}

const LoginScreen = ({ onLogin, password, setPassword, logging }) => (
  <div data-testid="admin-login" className="min-h-screen bg-[#0A0A0A] pt-24 pb-20 flex items-center">
    <div className="max-w-md mx-auto w-full px-6">
      <div className="bg-[#121212] border border-white/10 p-10">
        <div className="w-12 h-12 grid place-items-center bg-[#CCFF00] text-black mb-6">
          <Lock className="w-5 h-5" strokeWidth={2} />
        </div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#CCFF00] mb-2">Admin</p>
        <h1 className="font-display text-4xl uppercase tracking-tighter">Control Room</h1>
        <p className="text-white/60 mt-3 text-sm">Enter the admin password to manage bookings.</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onLogin(password);
          }}
          className="mt-6 space-y-4"
        >
          <Input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="bg-[#0A0A0A] border-white/20 rounded-none text-white focus:border-[#CCFF00] h-12"
          />
          <Button
            data-testid="admin-login-btn"
            type="submit"
            disabled={logging}
            className="w-full rounded-none bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#D4FF33] h-12"
          >
            {logging ? "Signing in..." : "Enter"}
          </Button>
        </form>
      </div>
    </div>
  </div>
);

const Dashboard = ({ token, onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterDate, setFilterDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterDate) params.date = filterDate;
      if (filterStatus) params.status = filterStatus;
      const [{ data: list }, { data: s }] = await Promise.all([
        adminListBookings(token, params),
        adminStats(token),
      ]);
      setBookings(list);
      setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Failed to load");
      }
    } finally {
      setLoading(false);
    }
  }, [token, filterDate, filterStatus, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await adminUpdateBooking(token, id, status);
      toast.success(`Booking ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#CCFF00] mb-2">Admin</p>
            <h1 className="font-display text-5xl lg:text-6xl uppercase tracking-tighter">Control Room</h1>
          </div>
          <Button
            data-testid="admin-logout-btn"
            onClick={onLogout}
            variant="ghost"
            className="rounded-none border border-white/15 hover:bg-white/5 text-white"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.8} /> Log out
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          <StatCard label="Total" value={stats?.total ?? "—"} testid="stat-total" />
          <StatCard label="Today" value={stats?.today ?? "—"} testid="stat-today" />
          <StatCard label="Confirmed" value={stats?.confirmed ?? "—"} testid="stat-confirmed" accent />
          <StatCard label="Cancelled" value={stats?.cancelled ?? "—"} testid="stat-cancelled" />
          <StatCard label="Revenue" value={stats ? `₹${stats.revenue}` : "—"} testid="stat-revenue" accent />
        </div>

        {/* Filters */}
        <div className="bg-[#121212] border border-white/10 p-5 mb-6 flex flex-wrap gap-3 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2">Date</div>
            <Input
              data-testid="filter-date"
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="bg-[#0A0A0A] border-white/20 rounded-none text-white focus:border-[#CCFF00]"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2">Status</div>
            <select
              data-testid="filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-[#0A0A0A] border border-white/20 text-white px-3 h-10 rounded-none focus:border-[#CCFF00] outline-none uppercase text-xs tracking-widest"
            >
              <option value="">All</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <Button
            data-testid="filter-clear"
            onClick={() => {
              setFilterDate("");
              setFilterStatus("");
            }}
            variant="ghost"
            className="rounded-none border border-white/15 text-white hover:bg-white/5"
          >
            Clear
          </Button>
          <Button
            data-testid="filter-refresh"
            onClick={load}
            className="rounded-none bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#D4FF33]"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} strokeWidth={1.8} />
            Refresh
          </Button>
        </div>

        {/* Table */}
        <div className="bg-[#121212] border border-white/10 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/50 uppercase tracking-widest text-[10px]">ID</TableHead>
                <TableHead className="text-white/50 uppercase tracking-widest text-[10px]">Date</TableHead>
                <TableHead className="text-white/50 uppercase tracking-widest text-[10px]">Time</TableHead>
                <TableHead className="text-white/50 uppercase tracking-widest text-[10px]">Player</TableHead>
                <TableHead className="text-white/50 uppercase tracking-widest text-[10px]">Contact</TableHead>
                <TableHead className="text-white/50 uppercase tracking-widest text-[10px]">Price</TableHead>
                <TableHead className="text-white/50 uppercase tracking-widest text-[10px]">Status</TableHead>
                <TableHead className="text-white/50 uppercase tracking-widest text-[10px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.map((b) => (
                <TableRow key={b.id} data-testid={`admin-row-${b.id}`} className="border-white/5 hover:bg-white/[0.02]">
                  <TableCell className="font-mono text-xs text-white/60">{b.id.slice(0, 8)}</TableCell>
                  <TableCell className="text-white">{b.date}</TableCell>
                  <TableCell className="text-white">
                    {b.hour_label}
                    {b.is_peak && <span className="ml-2 text-[10px] uppercase text-[#FF9F0A]">Peak</span>}
                  </TableCell>
                  <TableCell className="text-white">{b.name}</TableCell>
                  <TableCell className="text-white/70 text-xs">
                    <div>{b.phone}</div>
                    <div className="text-white/40">{b.email || "—"}</div>
                  </TableCell>
                  <TableCell className="text-white">₹{b.price}</TableCell>
                  <TableCell>
                    {b.status === "confirmed" ? (
                      <Badge className="bg-[#CCFF00] text-black uppercase tracking-widest rounded-none hover:bg-[#CCFF00]">Confirmed</Badge>
                    ) : (
                      <Badge className="bg-[#FF3B30] text-white uppercase tracking-widest rounded-none hover:bg-[#FF3B30]">Cancelled</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {b.status === "confirmed" ? (
                      <Button
                        data-testid={`cancel-${b.id}`}
                        size="sm"
                        onClick={() => updateStatus(b.id, "cancelled")}
                        className="rounded-none bg-transparent border border-[#FF3B30]/60 text-[#FF3B30] hover:bg-[#FF3B30]/10 uppercase text-[10px] tracking-widest"
                      >
                        <Ban className="w-3 h-3 mr-1" strokeWidth={2} />
                        Cancel
                      </Button>
                    ) : (
                      <Button
                        data-testid={`reactivate-${b.id}`}
                        size="sm"
                        onClick={() => updateStatus(b.id, "confirmed")}
                        className="rounded-none bg-transparent border border-[#CCFF00]/60 text-[#CCFF00] hover:bg-[#CCFF00]/10 uppercase text-[10px] tracking-widest"
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" strokeWidth={2} />
                        Reactivate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {bookings.length === 0 && !loading && (
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableCell colSpan={8} className="text-center py-10 text-white/40 uppercase tracking-widest text-xs">
                    No bookings match
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, testid, accent }) => (
  <div
    data-testid={testid}
    className={`p-5 border ${accent ? "border-[#CCFF00]/30 bg-[#0E0F08]" : "border-white/10 bg-[#121212]"}`}
  >
    <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</div>
    <div className={`font-display text-4xl mt-2 ${accent ? "text-[#CCFF00]" : "text-white"}`}>{value}</div>
  </div>
);
